const API_BASE = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL || process.env.VITE_API_BASE_URL || process.env.VITE_API_URL || '';
const PUBLIC_API_KEY = process.env.REACT_APP_PUBLIC_API_KEY || process.env.VITE_PUBLIC_API_KEY || '';
const EVENT_LOGS_PATH = process.env.REACT_APP_EVENT_LOGS_PATH || process.env.VITE_EVENT_LOGS_PATH || '/api/v1/event-logs';

const ANON_KEY = 'solvetax_anon_customer_id';
const SESSION_KEY = 'solvetax_session_id';
const JOURNEY_KEY_PREFIX = 'solvetax_journey_id:';
const RELEASE_TAG = process.env.REACT_APP_RELEASE_TAG || process.env.VITE_RELEASE_TAG || null;
const APP_VERSION = process.env.REACT_APP_APP_VERSION || process.env.VITE_APP_VERSION || RELEASE_TAG;

const uuid = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
    const r = (Math.random() * 16) | 0;
    const v = ch === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const getOrCreate = (storage, key, factory) => {
  try {
    const existing = storage.getItem(key);
    if (existing) return existing;
    const value = factory();
    storage.setItem(key, value);
    return value;
  } catch {
    return factory();
  }
};

const getAnonymousCustomerId = () => getOrCreate(localStorage, ANON_KEY, () => uuid().replace(/-/g, '').slice(0, 32));
const getSessionId = () => getOrCreate(sessionStorage, SESSION_KEY, uuid);

const parseBrowser = (uaRaw) => {
  const ua = uaRaw || '';
  const rules = [
    { name: 'EDGE', regex: /Edg\/([\d.]+)/i },
    { name: 'CHROME', regex: /Chrome\/([\d.]+)/i },
    { name: 'FIREFOX', regex: /Firefox\/([\d.]+)/i },
    { name: 'SAFARI', regex: /Version\/([\d.]+).*Safari/i },
  ];
  for (const item of rules) {
    const match = ua.match(item.regex);
    if (match) return { browser_name: item.name, browser_version: match[1] || null };
  }
  return { browser_name: null, browser_version: null };
};

const parseOs = (uaRaw) => {
  const ua = uaRaw || '';
  const rules = [
    { name: 'WINDOWS', regex: /Windows NT ([\d.]+)/i },
    { name: 'ANDROID', regex: /Android ([\d.]+)/i },
    { name: 'IOS', regex: /OS ([\d_]+) like Mac OS X/i, normalize: (v) => v.replace(/_/g, '.') },
    { name: 'MAC', regex: /Mac OS X ([\d_]+)/i, normalize: (v) => v.replace(/_/g, '.') },
    { name: 'LINUX', regex: /Linux/i, version: null },
  ];
  for (const item of rules) {
    const match = ua.match(item.regex);
    if (match) {
      const version = item.version !== undefined ? item.version : (item.normalize ? item.normalize(match[1] || '') : (match[1] || null));
      return { os_name: item.name, os_version: version };
    }
  }
  return { os_name: null, os_version: null };
};

const detectDeviceType = () => {
  const width = typeof window !== 'undefined' ? window.innerWidth : 0;
  if (width > 0 && width < 768) return 'PHONE';
  if (width >= 768 && width < 1024) return 'TABLET';
  return 'DESKTOP';
};

const detectPlatform = (uaRaw) => {
  const ua = uaRaw || '';
  if (/Android/i.test(ua)) return 'ANDROID';
  if (/iPhone|iPad|iPod/i.test(ua)) return 'IOS';
  return 'WEB';
};

const parseReferrerPath = () => {
  try {
    if (!document.referrer) return null;
    return new URL(document.referrer).pathname || null;
  } catch {
    return document.referrer || null;
  }
};

const getUtmParams = () => {
  try {
    const url = new URL(window.location.href);
    return {
      utm_source: url.searchParams.get('utm_source') || null,
      utm_medium: url.searchParams.get('utm_medium') || null,
      utm_campaign: url.searchParams.get('utm_campaign') || null,
      utm_content: url.searchParams.get('utm_content') || null,
      utm_term: url.searchParams.get('utm_term') || null,
    };
  } catch {
    return {
      utm_source: null,
      utm_medium: null,
      utm_campaign: null,
      utm_content: null,
      utm_term: null,
    };
  }
};

export const getJourneyId = (funnelName, { reset = false } = {}) => {
  if (!funnelName) return null;
  const key = `${JOURNEY_KEY_PREFIX}${funnelName}`;
  if (reset) {
    try {
      sessionStorage.removeItem(key);
    } catch {
      // ignore storage failures
    }
  }
  return getOrCreate(sessionStorage, key, uuid);
};

export const trackEvent = async (event) => {
  if (!API_BASE) return;
  const nowIso = new Date().toISOString();
  const userAgent = (typeof navigator !== 'undefined' && navigator.userAgent) ? navigator.userAgent : null;
  const { browser_name, browser_version } = parseBrowser(userAgent);
  const { os_name, os_version } = parseOs(userAgent);
  const utm = getUtmParams();
  const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : null;
  const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : null;
  const screenWidth = typeof window !== 'undefined' && window.screen ? window.screen.width : null;
  const screenHeight = typeof window !== 'undefined' && window.screen ? window.screen.height : null;
  const payload = {
    session_id: getSessionId(),
    anonymous_customer_id: getAnonymousCustomerId(),
    client_event_id: event.client_event_id || uuid(),
    event_name: event.event_name,
    event_category: event.event_category || 'ui',
    event_action: event.event_action || null,
    event_status: event.event_status || null,
    event_label: event.event_label || null,
    severity: event.severity || 'info',
    page_path: event.page_path || window.location.pathname,
    page_url: event.page_url || window.location.href,
    page_query: event.page_query || window.location.search || null,
    page_host: event.page_host || window.location.host || null,
    referrer_path: event.referrer_path || parseReferrerPath(),
    referrer_url: event.referrer_url || document.referrer || null,
    route_name: event.route_name || null,
    cta_name: event.cta_name || null,
    form_name: event.form_name || null,
    funnel_name: event.funnel_name || null,
    funnel_step_number: event.funnel_step_number ?? null,
    funnel_step_name: event.funnel_step_name || null,
    journey_id: event.journey_id || (event.funnel_name ? getJourneyId(event.funnel_name) : null),
    service_code: event.service_code || null,
    phone_number: event.phone_number || null,
    event_timestamp: event.event_timestamp || nowIso,
    dwell_time_seconds: event.dwell_time_seconds ?? null,
    active_time_seconds: event.active_time_seconds ?? null,
    api_name: event.api_name || null,
    api_status_code: event.api_status_code ?? null,
    api_response_time_ms: event.api_response_time_ms ?? null,
    error_code: event.error_code || null,
    error_message: event.error_message || null,
    ingestion_source: 'web_app',
    environment: process.env.NODE_ENV || 'development',
    release_tag: RELEASE_TAG,
    platform: event.platform || detectPlatform(userAgent),
    device_type: event.device_type || detectDeviceType(),
    os_name: event.os_name || os_name,
    os_version: event.os_version || os_version,
    browser_name: event.browser_name || browser_name,
    browser_version: event.browser_version || browser_version,
    app_version: event.app_version || APP_VERSION,
    user_agent: userAgent,
    viewport_width: event.viewport_width ?? viewportWidth,
    viewport_height: event.viewport_height ?? viewportHeight,
    screen_width: event.screen_width ?? screenWidth,
    screen_height: event.screen_height ?? screenHeight,
    language: event.language || (typeof navigator !== 'undefined' ? navigator.language : null),
    timezone_offset_min: event.timezone_offset_min ?? new Date().getTimezoneOffset(),
    ...utm,
  };
  try {
    const endpoint = `${API_BASE}${EVENT_LOGS_PATH}`;
    if (event.transport === 'beacon' && typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
      navigator.sendBeacon(endpoint, blob);
      return;
    }
    await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(PUBLIC_API_KEY ? { 'X-Public-Api-Key': PUBLIC_API_KEY } : {}),
      },
      body: JSON.stringify(payload),
      keepalive: true,
    });
  } catch {
    // no-op: telemetry should never block UX
  }
};

export const trackPageLifecycle = ({ funnel_name, form_name, service_code, open_event_name }) => {
  const startedAt = Date.now();
  const journeyId = funnel_name ? getJourneyId(funnel_name) : null;
  let leaveTracked = false;
  trackEvent({
    event_name: 'page_view',
    event_action: 'open',
    event_status: 'success',
    form_name,
    funnel_name,
    service_code,
    journey_id: journeyId,
  });
  if (open_event_name) {
    trackEvent({
      event_name: open_event_name,
      event_action: 'open',
      event_status: 'success',
      form_name,
      funnel_name,
      service_code,
      journey_id: journeyId,
      funnel_step_number: 1,
      funnel_step_name: 'form_open',
    });
  }

  const emitLeave = () => {
    if (leaveTracked) return;
    leaveTracked = true;
    const dwellSeconds = Math.max(0, Math.round((Date.now() - startedAt) / 1000));
    trackEvent({
      event_name: 'page_leave',
      event_action: 'leave',
      event_status: 'success',
      form_name,
      funnel_name,
      service_code,
      journey_id: journeyId,
      dwell_time_seconds: dwellSeconds,
      active_time_seconds: dwellSeconds,
      transport: 'beacon',
    });
  };

  const onVisibilityChange = () => {
    if (document.visibilityState === 'hidden') {
      emitLeave();
    }
  };

  window.addEventListener('pagehide', emitLeave);
  window.addEventListener('beforeunload', emitLeave);
  document.addEventListener('visibilitychange', onVisibilityChange);

  return () => {
    window.removeEventListener('pagehide', emitLeave);
    window.removeEventListener('beforeunload', emitLeave);
    document.removeEventListener('visibilitychange', onVisibilityChange);
    emitLeave();
  };
};
